import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import InitialsAvatar from 'design-system/src/components/InitialsAvatar/InitialsAvatar';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Typography } from 'design-system/src/components/Typography/Typography';
import {
  PopoverMenu,
  PopoverMenuSection,
  PopoverMenuItems,
  PopoverMenuItem,
  PopoverMenuItemButton,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import HorizontalMenu from 'design-system/src/assets/icons/horizontalMenu.svg?react';
import { Table } from '../../Table';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { noResultsCopy } from 'design-system/src/sharedCopy';
import { useProviders } from '../../../hooks/useProviders';
import AddOrEditProviderModal from '../AddOrEditProviderModal/AddOrEditProviderModal';
import RemoveProviderModal from '../RemoveProviderModal/RemoveProviderModal';
import { TableNullState, TableNullStateType } from '../../TableNullState/TableNullState';
import { parseProviderName } from '../utils';
import { createTrackingProps } from 'shared-utils/analytics';
// TODO: move these shared styled components to a shared location
import {
  StyledSearchField,
  CustomTableContainer,
  CustomTableRow,
  CustomTableCell,
  Name,
  EmailAndName,
  RightmostCell,
  IconPopoverMenuButton,
} from '../UsersTable';

const NpiCell = styled(CustomTableCell)`
  width: 100%;
`;

const ProviderActionsPopoverMenu: React.FC<{
  onSelectEditProvider: () => void;
  onSelectRemoveProvider: () => void;
}> = ({ onSelectEditProvider, onSelectRemoveProvider }) => {
  return (
    <PopoverMenu>
      <IconPopoverMenuButton>
        <HorizontalMenu title="actions" />
      </IconPopoverMenuButton>
      <PopoverMenuItems width="202px">
        <PopoverMenuSection>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={() => {
                  itemProps.close?.();
                  onSelectEditProvider();
                }}
                {...createTrackingProps({ elementName: 'Providers Table: Edit Provider' })}
              >
                Edit provider information
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={() => {
                  itemProps.close?.();
                  onSelectRemoveProvider();
                }}
                {...createTrackingProps({ elementName: 'Providers Table: Remove Provider' })}
              >
                Remove provider
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
        </PopoverMenuSection>
      </PopoverMenuItems>
    </PopoverMenu>
  );
};

/**
 * Table of all providers in an organization.
 */
const ProvidersTable: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [showEditProviderModal, setShowEditProviderModal] = useState(false);
  const [showRemoveProviderModal, setShowRemoveProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<{
    provider_id: string;
    npi: string;
    first_name: string;
    last_name: string;
  } | null>(null);

  const { data, isLoading, error } = useProviders();
  const providers = useMemo(() => data?.providers ?? [], [data]);

  const filteredProviders = useMemo(
    () =>
      providers.filter((provider) => {
        const query = searchQuery.toLowerCase();
        const nameMatch = provider.name?.toLowerCase().includes(query);
        const npiMatch = provider.npi.includes(query);
        return nameMatch || npiMatch;
      }),
    [providers, searchQuery],
  );

  const existingNpis = useMemo(() => providers.map((p) => p.npi), [providers]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <PageLoadError />;
  }

  const handleEditProvider = (provider: { provider_id: string; npi: string; name: string | null }) => {
    const { first_name, last_name } = parseProviderName(provider.name);
    setSelectedProvider({
      provider_id: provider.provider_id,
      npi: provider.npi,
      first_name,
      last_name,
    });
    setShowEditProviderModal(true);
  };

  const closeEditModal = () => {
    setShowEditProviderModal(false);
    setSelectedProvider(null);
  };

  const handleRemoveProvider = (provider: { provider_id: string; npi: string; name: string | null }) => {
    const { first_name, last_name } = parseProviderName(provider.name);
    setSelectedProvider({
      provider_id: provider.provider_id,
      npi: provider.npi,
      first_name,
      last_name,
    });
    setShowRemoveProviderModal(true);
  };

  const closeRemoveModal = () => {
    setShowRemoveProviderModal(false);
    setSelectedProvider(null);
  };

  return (
    <>
      {providers.length === 0 && !searchQuery ? (
        <TableNullState
          nullStateType={TableNullStateType.NO_PROVIDERS}
          onAction={() => setShowAddProviderModal(true)}
        />
      ) : (
        <>
          <ButtonRow align={Alignment.SpaceBetween}>
            <StyledSearchField
              label="Search by provider name or NPI"
              placeholder="Search by provider name or NPI"
              hideLabel
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
              {...createTrackingProps({ elementName: 'Providers Table: Search' })}
            />

            <Button
              onClick={() => setShowAddProviderModal(true)}
              {...createTrackingProps({ elementName: 'Providers Table: Add Provider Button' })}
            >
              Add a provider
            </Button>
          </ButtonRow>

          <CustomTableContainer>
            <Table>
              <thead className="sr-only">
                <tr>
                  <td>Name and NPI</td>
                  <td>Actions</td>
                </tr>
              </thead>
              <tbody>
                {filteredProviders.length === 0 ? (
                  <tr>
                    <CustomTableCell colSpan={2}>{noResultsCopy}</CustomTableCell>
                  </tr>
                ) : (
                  filteredProviders.map((provider) => {
                    return (
                      <CustomTableRow key={provider.provider_id}>
                        <NpiCell>
                          <Name>
                            <InitialsAvatar name={provider.name ?? provider.npi} />
                            <EmailAndName>
                              <Typography renderedAs="span" styledAs="bodySmallDMSans" weight={600}>
                                {provider.name}
                              </Typography>
                              <Typography
                                renderedAs="span"
                                styledAs="bodyExtraSmallDMSans"
                                weight={provider.name ? 400 : 600}
                              >
                                {provider.npi}
                              </Typography>
                            </EmailAndName>
                          </Name>
                        </NpiCell>

                        <RightmostCell>
                          <ProviderActionsPopoverMenu
                            onSelectEditProvider={() => handleEditProvider(provider)}
                            onSelectRemoveProvider={() => handleRemoveProvider(provider)}
                          />
                        </RightmostCell>
                      </CustomTableRow>
                    );
                  })
                )}
              </tbody>
            </Table>
          </CustomTableContainer>
        </>
      )}

      <AddOrEditProviderModal
        open={showAddProviderModal}
        mode="add"
        existingNpis={existingNpis}
        onClose={() => setShowAddProviderModal(false)}
      />

      {selectedProvider && (
        <>
          <AddOrEditProviderModal
            open={showEditProviderModal}
            mode="edit"
            provider={selectedProvider}
            onClose={closeEditModal}
          />

          <RemoveProviderModal open={showRemoveProviderModal} provider={selectedProvider} onClose={closeRemoveModal} />
        </>
      )}
    </>
  );
};

export default ProvidersTable;
